import { css } from '@emotion/react';
import { Button, Card, color, Icon, Modal, spacing, Typography } from '@uniquegood/realworld-studio-design';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { adminApi } from '@/api';
import { useModalState } from '@/hooks';
import { ModalChildrenStyles } from '../styles';
import { MarginBottom10 } from '@/styles';

const SCHOOL_ADMIN_CHANNEL_ID = 'FdnJHJmjfI37IJkK13IPHw';

function TransferOwnership() {
  const { appId, projectId } = useParams<AppParam>();
  const [isTransferring, setIsTransferring] = useState(false);
  const { modal: confirmModal, openModal: openConfirmModal, closeModal: closeConfirmModal } = useModalState();
  const { modal: successModal, openModal: openSuccessModal, closeModal: closeSuccessModal } = useModalState();

  const handleTransfer = useCallback(async () => {
    closeConfirmModal();
    setIsTransferring(true);
    try {
      await adminApi.patch(
        `/manage/api/projects/${projectId}/application`,
        { applicationId: SCHOOL_ADMIN_CHANNEL_ID },
        { headers: { 'x-rwd-api-version': '1.0' } }
      );
      openSuccessModal({
        title: '소유권 이전이 완료되었어요',
        cssStyle: ModalChildrenStyles,
        primaryAction: {
          content: '확인',
          onAction: () => {
            closeSuccessModal();
            window.location.href = `/apps/${appId}`;
          }
        },
        children: (
          <>
            <Icon
              cssStyle={MarginBottom10}
              icon="check_circle_regular"
              size="100px"
              color={color.interactive_primary_subdued}
            />
            이 콘텐츠의 소유권이 스쿨 어드민 채널로 이전되었어요.
          </>
        )
      });
    } catch (error) {
      // 전역 에러 토스트가 자동으로 표시됨
    } finally {
      setIsTransferring(false);
    }
  }, [appId, projectId, closeConfirmModal, openSuccessModal, closeSuccessModal]);

  const handleClick = useCallback(() => {
    openConfirmModal({
      title: '콘텐츠 소유권을 이전하시겠어요?',
      cssStyle: ModalChildrenStyles,
      children: (
        <div css={descriptionStyle}>
          이 콘텐츠의 소유권이 <strong>리얼월드 스쿨 어드민 채널</strong>로 이전됩니다.
          <br />
          이전 후에는 현재 채널에서 이 콘텐츠를 수정할 수 없게 됩니다.
        </div>
      ),
      primaryAction: {
        content: '이전하기',
        onAction: handleTransfer,
        destructive: true
      },
      secondaryAction: {
        content: '취소',
        onAction: closeConfirmModal
      }
    });
  }, [openConfirmModal, closeConfirmModal, handleTransfer]);

  return (
    <>
      <Card
        header={{
          title: '콘텐츠 소유권 이전',
          prefix: <Icon icon="arrow_right_arrow_left_regular" />
        }}
      >
        <div css={containerStyle}>
          <Typography type="body" cssStyle={textStyle}>
            이 콘텐츠를 스쿨 어드민 채널로 이전할 수 있어요. 이전 후에는 현재 채널에서 수정할 수 없게 됩니다.
          </Typography>
          <Button
            onClick={handleClick}
            loading={isTransferring}
            type="destructive"
          >
            소유권 이전
          </Button>
        </div>
      </Card>
      <Modal {...confirmModal} />
      <Modal {...successModal} />
    </>
  );
}

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.medium};
  align-items: flex-start;
`;

const textStyle = css`
  color: ${color.text_subdued};
  font-size: 14px;
  line-height: 20px;
`;

const descriptionStyle = css`
  text-align: center;
  font-size: 14px;
  line-height: 22px;
  color: ${color.text_subdued};

  strong {
    color: ${color.text_default};
    font-weight: 600;
  }
`;

export default TransferOwnership;
